<div class="commission-container">
  <div class="header-section">
    <h1>Sales Commission Structure</h1>
    <p class="subtitle">Door-to-Door Sales Opportunity</p>
  </div>

  <div class="content-grid">
    <!-- Commission Structure -->
    <div class="commission-card full-width">
      <h2>Our Commission Promise</h2>
      <div class="commission-highlight">
        <div class="percentage-display">
          <span class="percentage">50%</span>
          <span class="percentage-label">of Profit</span>
        </div>
        <div class="commission-details">
          <h3>Simple. Fair. Ongoing.</h3>
          <p>You earn 50% of the profit on every sale you make for as long as you're with Complete Pest Control.</p>
          <div class="key-points">
            <div class="point">
              <span class="point-icon">💰</span>
              <span>No tiers, no caps, no complexity</span>
            </div>
            <div class="point">
              <span class="point-icon">🔄</span>
              <span>Residual earnings on your sales as long as you are selling with us!</span>
            </div>
            <div class="point">
              <span class="point-icon">📈</span>
              <span>The more you sell, the more you earn</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Monthly Bonuses -->
    <div class="commission-card full-width">
      <h2>Why 50% of Profit Works</h2>
      <div class="benefits-list">
        <div class="benefit-item">
          <div class="benefit-icon">🎯</div>
          <div class="benefit-content">
            <h4>Aligned Interests</h4>
            <p>We succeed when you succeed. The more profitable your sales, the more you earn.</p>
          </div>
        </div>
        <div class="benefit-item">
          <div class="benefit-icon">⏰</div>
          <div class="benefit-content">
            <h4>Ongoing Earnings</h4>
            <p>Continue earning from your customers as long as you both remain with Complete Pest Control.</p>
          </div>
        </div>
        <div class="benefit-item">
          <div class="benefit-icon">📊</div>
          <div class="benefit-content">
            <h4>Transparent & Fair</h4>
            <p>No complex calculations or changing rules. Simple 50% split on every profitable sale.</p>
          </div>
        </div>
        <div class="benefit-item">
          <div class="benefit-icon">🚀</div>
          <div class="benefit-content">
            <h4>Unlimited Potential</h4>
            <p>No caps on earnings. The sky's the limit on your income potential.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Earnings Calculator -->
    <div class="commission-card calculator-card">
      <h2>Earnings Calculator</h2>
      <div class="calculator">
        <div class="year-one-container">
          <div class="year-header">
            <div class="year-label">{{totalYears() === 1 ? 'Year One' : totalYears() + ' Year Projection'}}</div>
            <div class="header-controls">
              <button mat-icon-button class="add-year-btn" (click)="addYear()" title="Add Another Year">
                <mat-icon>add</mat-icon>
              </button>
              <button mat-icon-button class="settings-toggle" (click)="toggleAdvancedSettings()"
                title="Advanced Settings">
                <mat-icon>tune</mat-icon>
              </button>
            </div>
          </div>

          <div class="calculator-inputs-container">
            <div class="default-inputs">
              <div class="large-input-group">
                <label for="yearOneSales">Total Sales</label>
                <input type="number" id="yearOneSales" [(ngModel)]="yearOneSalesValue" min="0"
                  class="large-number-input" placeholder="0">
              </div>

              <div class="large-input-group">
                <label for="avgMonthlyPrice">Avg Monthly Price</label>
                <div class="currency-input">
                  <span class="currency-symbol">$</span>
                  <input type="number" id="avgMonthlyPrice" [(ngModel)]="avgMonthlyPriceValue" min="0"
                    class="large-number-input" placeholder="0">
                </div>
              </div>

              <!-- Year One Commission Summary -->
              <div class="year-commission">
                <span class="commission-label">Total Customers: {{yearOneSales()}}</span>
                <span class="commission-label">Commission:</span>
                <span class="commission-value">${{yearOneCommission() | number:'1.0-0'}}</span>
              </div>
            </div>

            <!-- Additional Years -->
            @if (additionalYears().length > 0) {
              <div class="additional-years">
                @for (year of additionalYears(); track $index) {
                  <div class="additional-year">
                    <div class="year-header-small">
                      <span class="year-title">Year {{$index + 2}}</span>
                      <button mat-icon-button class="remove-year-btn" (click)="removeYear($index)" title="Remove Year">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                    <div class="year-inputs">
                      <div class="small-input-group">
                        <label>Sales</label>
                        <input type="number" [value]="year.sales" (input)="updateYearSales($index, +$any($event.target).value)"
                          min="0" class="large-number-input" placeholder="0">
                      </div>
                      <div class="small-input-group">
                        <label>Avg Price</label>
                        <div class="small-currency-input">
                          <span class="currency-symbol">$</span>
                          <input type="number" [value]="year.avgPrice"
                            (input)="updateYearPrice($index, +$any($event.target).value)" min="0" class="large-number-input"
                            placeholder="0">
                        </div>
                      </div>
                      <div class="year-commission">
                        <span class="commission-label">Total Customers: {{getYearTotalCustomers(year, $index)}}</span>
                        <span class="commission-label">Commission:</span>
                        <span class="commission-value">${{getYearCommission(year, $index) | number:'1.0-0'}}</span>
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>

          <div class="total-commission-display">
            <div class="commission-label">Total Commission</div>
            <div class="commission-amount">${{totalCommissionAllYears() | number:'1.0-0'}}</div>
            <div class="commission-subtitle">{{totalYears() === 1 ? 'Year One Earnings' : totalYears() + ' Year Total'}}
            </div>
          </div>

          <div class="calculation-breakdown">
            <div class="breakdown-item">
              <span class="breakdown-label">Annual Revenue Generated:</span>
              <span class="breakdown-value">${{annualRevenue() | number:'1.0-0'}}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Annual Costs (${{monthlyCost()}}/month/customer):</span>
              <span class="breakdown-value">${{annualCosts() | number:'1.0-0'}}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">Total Profit:</span>
              <span class="breakdown-value">${{annualProfit() | number:'1.0-0'}}</span>
            </div>
            <div class="breakdown-item highlight">
              <span class="breakdown-label">Your 50% Share:</span>
              <span class="breakdown-value">${{yearOneCommission() | number:'1.0-0'}}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Requirements & Benefits -->
    <div class="commission-card">
      <h2>Requirements & Benefits</h2>
      <div class="requirements-grid">
        <div class="requirements-section">
          <h3>Requirements</h3>
          <ul>
            <li>Valid driver's license</li>
            <li>Reliable transportation</li>
            <li>Professional appearance</li>
            <li>Strong communication skills</li>
          </ul>
        </div>

        <div class="benefits-section">
          <h3>Benefits</h3>
          <ul>
            <li>Flexible schedule</li>
            <li>Uncapped earning potential</li>
            <li>Monthly commission payments</li>
            <li>Performance recognition program</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Territory Information -->
    <div class="commission-card">
      <h2>Territory & Schedule</h2>
      <div class="territory-info">
        <div class="territory-section">
          <h3>Coverage Areas</h3>
          <p>Salt Lake County and Davis County</p>
        </div>

        <div class="schedule-section">
          <h3>Typical Schedule</h3>
          <div class="schedule-grid">
            <div class="schedule-item">
              <strong>Peak Hours:</strong>
              <span>4:00 PM - 8:00 PM</span>
            </div>
            <div class="schedule-item">
              <strong>Saturday:</strong>
              <span>10:00 AM - 6:00 PM</span>
            </div>
            <div class="schedule-item">
              <strong>Minimum:</strong>
              <span>No minimums!</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contractor Information -->
    <div class="commission-card">
      <h2>Contractor Support</h2>
      <div class="contracting-info">
        <div class="contracting-section">
          <h3>We support our contractors!</h3>
          <p>Sales portal to see your sales and commissions anytime.</p>
          <p></p>
        </div>
      </div>
    </div>
  </div>

  <!-- Call to Action -->
  <div class="cta-section">
    <h2>Ready to Earn 50% of Every Sale?</h2>
    <p>Join our team and start building residual income with Complete Pest Control</p>
  </div>
</div>